<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useDateFormat } from '@vueuse/core'
  import { useAudiobooksMenuStore } from '@/stores/list-item-menu'
  import { useAudiobooksStore } from '@/stores/audiobooks'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useBrowserStore } from '@/stores/browser'
  import type { TProjectAudiobook } from '@/types/audiobooks'
  import DesHover from '@/components/DesHover.vue'
  import DesHoverMenu from '@/components/DesHoverMenu.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'
  import DesEditName from '@/components/DesEditName.vue'

  const props = defineProps<{
    item: TProjectAudiobook
    disableCheckboxes?: boolean
  }>()

  const audiobooks = useAudiobooksStore()
  const snack = useSnackbarStore()
  const browser = useBrowserStore()
  const { open } = useAudiobooksMenuStore()

  const isProcessing = computed(() => props.item.status === 1 || props.item.status === 3)
  const isFailed = computed(() => props.item.status === 4)
  const chaptersIncomplete = computed(() => props.item.status === 2 && props.item.chapters_state === 'partial')
  const chaptersChanged = computed(() => props.item.status === 2 && props.item.chapters_in_sync === false)

  const formatDate = (date: string) => useDateFormat(date, 'MMMM DD, YYYY', { locales: 'en-US' })
  const tile = ref<HTMLElement | null>(null)

  const edit = async (project: TProjectAudiobook) => {
    if (audiobooks.isInProgress(project)) {
      snack.add('Processing is still in progress')
      return
    }

    if (project.status === 4) {
      snack.add(audiobooks.getErrorForAudioBookStatus(project.status), { type: 'danger' })
      return
    }

    const url = await audiobooks.getAudiobookPreviewUrl(project)
    await browser.openNewWindowTab(url)
  }

  const renameProject = async (project: TProjectAudiobook, name: string) => {
    const oldName = project.name
    project.name = name

    const res = await audiobooks.renameAudiobook(project.id, project.name)
    if (res.status === 'error') {
      snack.add(res.message, { type: 'danger' })
      project.name = oldName
    }
  }
</script>

<template>
  <div class="flex flex-col gap-1.5" ref="tile">
    <div
      class="relative h-52 px-4 pt-4 bg-white-gray rounded-xl justify-center items-start inline-flex overflow-hidden cursor-pointer"
    >
      <div class="h-64 flex-col justify-start items-center inline-flex" :class="{ grayscale: item.status !== 2 }">
        <img
          v-once
          :src="'/dashboard/elements/audiobook_cover.svg'"
          class="rounded-md max-h-full max-w-full w-auto h-auto shadow-sm"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
      </div>
      <div
        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center mt-8"
        v-if="isProcessing"
      >
        <des-icon icon="fa-light fa-clock" />
        <span>Processing...</span>
      </div>
      <div
        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center mt-8 text-red-50"
        v-else-if="isFailed"
      >
        <des-icon icon="fa-light fa-circle-exclamation" />
        <span>Failed</span>
      </div>
      <div
        v-if="chaptersIncomplete"
        class="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-orange-95 text-orange-30 text-xs font-medium flex items-center gap-1"
        v-des-tooltip="'Some chapter files did not upload; open the menu to retry'"
      >
        <des-icon icon="fa-light fa-triangle-exclamation" class="w-3 h-3" />
        Chapter files incomplete
      </div>
      <div
        v-else-if="chaptersChanged"
        class="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-blue-95 text-blue-30 text-xs font-medium flex items-center gap-1"
        v-des-tooltip="'The chapter breaks changed since this audiobook was published'"
      >
        <des-icon icon="fa-light fa-bookmark" class="w-3 h-3" />
        Chapters changed
      </div>
      <DesHover data-sortable-handler @click.stop="edit(item)">
        <DesHoverMenu>
          <DesButtonIcon v-if="item.status === 2" @click.stop="edit(item)" icon="fa-light fa-eye" />
          <DesButtonIcon @click.stop="tile && open(item, tile)" icon="fa-light fa-ellipsis" />
        </DesHoverMenu>
      </DesHover>
    </div>
    <div>
      <DesEditName :name="item.name" @edit="(newValue: string) => renameProject(item, newValue)" />
      <div class="mt-1 text-black-40 text-xs leading-none">
        {{ item.updated_at ? formatDate(item.created_at).value : '' }}
      </div>
    </div>
  </div>
</template>
